/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef } from "react"
import types from "prop-types"
import { debounce, isEqual } from "lodash-es"
import { useDispatch, useSelector } from "react-redux"
import { organizationUsersById } from "reduxSlices/organizationUsersSlice"
import { getTaskUserIds } from "reduxSlices/formSubmissionSlice"
import { getDraftAnswer, setDraftAnswer } from "reduxSlices/taskDraftAnswersSlice"
import { questionShape } from "utils/propTypeShapes"
import { FormsyMentionableTextInput } from "shared/inputs"

// Multi-entry questions only appear on task forms (i.e., not public forms or surveys).
// So, in general, we expect to have access to a task that should be stored
// in redux and findable by the id passed as `taskId`.  However, the answer field
// must also function in the form builder preview mode.  In the preview mode,
// there is no access to a task or users and no need to store draft answers,
// so the components behavior does involve some conditional logic to handle
// the preview mode.
//
// We determine preview mode by the lack of a taskId prop value.

const MultiEntriesAnswerField = ({
  question,
  initialValue = null,
  disabled = false,
  taskId = undefined,
}) => {
  const dispatch = useDispatch()

  const { required, uuid } = question

  const answer = useRef(initialValue)

  const taskUserIds = useSelector(getTaskUserIds(taskId), isEqual)
  const activeTaskUsers = useSelector(organizationUsersById(taskUserIds, { excludeInactive: true }), isEqual)

  // We don't store draft answers when a user previews the form in the form builder,
  const storeDraft = useCallback(debounce(
    (value) => {
      if (taskId) dispatch(setDraftAnswer({ answer: value, questionUuid: uuid, taskId }))
    },
    500,
    { leading: true, trailing: true },
  ), [dispatch, setDraftAnswer])

  const draftAnswer = useSelector(getDraftAnswer({ questionUuid: uuid, taskId }))

  useEffect(() => {
    if (draftAnswer !== null && draftAnswer !== undefined) {
      answer.current = draftAnswer
    }
  }, [draftAnswer, initialValue])

  return (
    <FormsyMentionableTextInput
      className="multi-entries-answer-field"
      disabled={disabled}
      name={uuid}
      onChangeSideEffect={storeDraft}
      placeholder="Add an answer ..."
      required={required}
      users={activeTaskUsers}
      validations={{
        isNotBlankString: required,
        noExcelMetacharacters: true,
        noHTML: true,
      }}
      value={answer.current || ""}
    />
  )
}

MultiEntriesAnswerField.propTypes = {
  disabled: types.bool,
  initialValue: types.string,
  question: questionShape.isRequired,
  taskId: types.number,
}

export default MultiEntriesAnswerField
